import { useAsyncState } from '@vueuse/core';
import { ref, shallowRef } from 'vue';

export interface IUseAsyncStateParams {
    shallow?: boolean,
    resetOnExecute?: boolean,
    immediate?: boolean
}

export function usePromise<Data, Params extends any[] = []>(
    promise: Promise<Data> | ((...args: Params) => Promise<Data>),
    initialState: Data,
    options?: IUseAsyncStateParams,
) {
    const {
        shallow = false,
        resetOnExecute = true,
        immediate = false
    } = options ?? {}

    const data = shallow ? shallowRef(initialState) : ref(initialState);
    const isLoading = ref(false);
    const error = shallowRef<unknown | undefined>(undefined);

    let currentRequestId = 0;

    async function execute(...args: any[]) {
        const requestId = ++currentRequestId;

        if (resetOnExecute)
            data.value = initialState;

        error.value = undefined;
        isLoading.value = true;

        const _promise = typeof promise === 'function'
            ? promise(...args as Params)
            : promise;

        try {
            const result = await _promise;

            if (requestId === currentRequestId) {
                data.value = result;
            }
        }
        catch (e) {
            if (requestId === currentRequestId) {
                error.value = e;
            }
        }
        finally {
            if (requestId === currentRequestId) {
                isLoading.value = false;
            }
        }

        return data.value as Data;
    }

    if (immediate)
        execute();

    return {
        data, isLoading, error, execute
    }
}